/**
 * Venda pelo chat (Prompt 06): carrinho da conversa e pedido no ERP.
 * O ERP é dono de preço, estoque e pedido; o chat monta o carrinho e
 * acompanha o status.
 */
import { z } from "zod";
import type { EnderecoErp, OpcoesVendaErp, StatusPedidoChat, VendedorErp } from "./erp";

export interface ItemVenda {
  variacaoId: string;
  produtoId: string;
  nome: string;
  sku: string | null;
  cor: string | null;
  tamanho: string | null;
  fotoUrl: string | null;
  /** Preço do ERP no momento em que entrou no carrinho (o ERP confere de novo). */
  preco: number;
  quantidade: number;
  /** Desconto do item, em reais (total da linha). */
  desconto: number;
  estoqueDisponivel: number;
}

export interface EntregaVenda {
  modo: "retirada" | "entrega";
  transportadorId: string | null;
  transportadorNome: string | null;
  frete: number;
  /** Nulo = endereço do cadastro da cliente. */
  endereco: Partial<EnderecoErp> | null;
}

export const STATUS_VENDA = ["rascunho", "enviando", "criada", "erro"] as const;
export type StatusVenda = (typeof STATUS_VENDA)[number];

export interface Venda {
  id: string;
  conversaId: string;
  status: StatusVenda;
  itens: ItemVenda[];
  desconto: number;
  entrega: EntregaVenda;
  formaPagamento: string | null;
  observacao: string | null;
  subtotal: number;
  total: number;
  pedido: {
    id: string;
    numero: number | null;
    status: StatusPedidoChat;
    total: number | null;
  } | null;
  erro: string | null;
  criadoPor: { id: string; nome: string } | null;
  /** Vendedora do ERP escolhida no carrinho; nulo = a vinculada a quem atende. */
  vendedoraErpId: string | null;
  atualizadoEm: string;
}

export interface OpcoesVenda extends OpcoesVendaErp {
  /** Desconto máximo (% do subtotal) para quem está logado; nulo = sem limite. */
  descontoMaximoPercentual: number | null;
  /** Vendedoras ativas do ERP (para escolher no carrinho). */
  vendedoras: VendedorErp[];
  /** Vendedora do ERP de quem está atendendo (Configurações → Integrações). */
  vendedoraVinculada: { erpId: string | null; atendente: string | null };
}

const itemSchema = z.object({
  variacaoId: z.string().min(1).max(100),
  produtoId: z.string().min(1).max(100),
  nome: z.string().max(200),
  sku: z.string().max(100).nullable(),
  cor: z.string().max(100).nullable(),
  tamanho: z.string().max(50).nullable(),
  fotoUrl: z.string().max(1000).nullable(),
  preco: z.number().min(0),
  quantidade: z.number().int().min(1).max(999),
  desconto: z.number().min(0),
  estoqueDisponivel: z.number(),
});

export const rascunhoVendaSchema = z.object({
  itens: z.array(itemSchema).max(50),
  desconto: z.number().min(0).default(0),
  entrega: z.object({
    modo: z.enum(["retirada", "entrega"]),
    transportadorId: z.string().max(100).nullable(),
    transportadorNome: z.string().max(200).nullable(),
    frete: z.number().min(0).max(10_000),
    endereco: z
      .object({
        cep: z.string().max(9).nullish(),
        logradouro: z.string().max(120).nullish(),
        numero: z.string().max(20).nullish(),
        complemento: z.string().max(120).nullish(),
        bairro: z.string().max(120).nullish(),
        cidade: z.string().max(120).nullish(),
        uf: z.string().max(2).nullish(),
      })
      .nullable(),
  }),
  formaPagamento: z.string().max(100).nullable(),
  observacao: z.string().max(500).nullable(),
  /** Nulo = a vendedora vinculada a quem atende. */
  vendedoraErpId: z.string().max(100).nullable().default(null),
});
export type RascunhoVenda = z.infer<typeof rascunhoVendaSchema>;

export const configVendasSchema = z.object({
  /** Desconto máximo que a vendedora concede sem a gerência (% do subtotal). */
  descontoMaximoVendedora: z.number().min(0).max(100).default(10),
});
export type ConfigVendas = z.infer<typeof configVendasSchema>;
export const CONFIG_VENDAS_PADRAO: ConfigVendas = configVendasSchema.parse({});

export function calcularTotais(v: {
  itens: Pick<ItemVenda, "preco" | "quantidade" | "desconto">[];
  desconto: number;
  entrega: { frete: number };
}) {
  const bruto = v.itens.reduce((s, i) => s + i.preco * i.quantidade, 0);
  const descontoItens = v.itens.reduce(
    (s, i) => s + Math.min(i.desconto, i.preco * i.quantidade),
    0,
  );
  const subtotal = arredondar(bruto - descontoItens);
  const desconto = arredondar(Math.min(v.desconto, subtotal));
  const total = arredondar(subtotal - desconto + v.entrega.frete);
  /** % de desconto total (itens + pedido) sobre o valor bruto. */
  const percentualDesconto = bruto ? ((descontoItens + desconto) / bruto) * 100 : 0;
  return { bruto: arredondar(bruto), subtotal, desconto, total, percentualDesconto };
}

const arredondar = (n: number) => Math.round(n * 100) / 100;

/** Etapa do funil da conversa conforme o pedido anda no ERP. */
export const ETAPA_POR_STATUS_PEDIDO: Record<
  StatusPedidoChat,
  "aguardando_pagamento" | "pagamento_confirmado" | "pedido_criado" | "pos_venda" | "negociacao"
> = {
  iniciado: "aguardando_pagamento",
  aguardando_pagamento: "aguardando_pagamento",
  pago: "pagamento_confirmado",
  em_separacao: "pagamento_confirmado",
  enviado: "pedido_criado",
  entregue: "pos_venda",
  cancelado: "negociacao",
};

export const ROTULO_STATUS_PEDIDO: Record<StatusPedidoChat, string> = {
  iniciado: "Pedido iniciado",
  aguardando_pagamento: "Aguardando pagamento",
  pago: "Pago",
  em_separacao: "Em separação",
  enviado: "Enviado",
  entregue: "Entregue",
  cancelado: "Cancelado",
};
